class AuthService {
  static subscribers = [];

  // ── Session-only enforcement (replaces static block) ──
  // Called once immediately after class definition (see bottom of file).
  // Desktop always stays logged in — never clears on startup.
  static _init() {
    const isDesktop = process.env.REACT_APP_ENV === "desktop";
    if (!isDesktop) {
      const stayLoggedIn = localStorage.getItem("stay_logged_in");
      // Only clear if explicitly set to "false" (user unticked)
      // "true" or null (default) = keep tokens
      if (stayLoggedIn === "false") {
        const clearedThisSession = sessionStorage.getItem("session_cleared");
        if (!clearedThisSession) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("cloud_access_token");
          localStorage.removeItem("cloud_refresh_token");
          sessionStorage.setItem("session_cleared", "true");
        }
      }
    }
  }

  static setTokens(accessToken, refreshToken) {
    localStorage.setItem("access_token",  accessToken);
    localStorage.setItem("refresh_token", refreshToken);
    sessionStorage.removeItem("session_cleared");
    this.notifySubscribers();
  }

  static clearTokens() {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("cloud_access_token");
    localStorage.removeItem("cloud_refresh_token");
    localStorage.removeItem("stay_logged_in");
    localStorage.removeItem("user_id");
    localStorage.removeItem("pharmacy_id");
    localStorage.removeItem("branch_id");
    localStorage.removeItem("staff_id");
    localStorage.removeItem("user_type");
    localStorage.removeItem("auth_mode");
    localStorage.removeItem("user_data");
    this.notifySubscribers();
  }

  static getAccessToken() {
    return localStorage.getItem("access_token");
  }

  static getRefreshToken() {
    return localStorage.getItem("refresh_token");
  }

  static isLoggedIn() {
    const token = localStorage.getItem("access_token");
    if (!token) return false;
    try {
      const payload = this.decodeToken(token);
      return !this.isTokenExpired(payload);
    } catch (err) {
      console.error("Invalid token:", err);
      return false;
    }
  }

  static decodeToken(token) {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch (err) {
      console.error("Error decoding token:", err);
      return null;
    }
  }

  static getUserId() {
    const token = this.getAccessToken();
    if (token) {
      const decoded = this.decodeToken(token);
      return decoded ? decoded.user_id : null;
    }
    return null;
  }

  static getUserInfo() {
    const token = this.getAccessToken();
    if (token) {
      const decoded = this.decodeToken(token);
      return decoded || null;
    }
    return null;
  }

  static subscribe(callback) {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter((cb) => cb !== callback);
    };
  }

  static notifySubscribers() {
    const userInfo   = this.getUserInfo();
    const isLoggedIn = !!userInfo && !this.isTokenExpired(userInfo);
    this.subscribers.forEach((cb) => cb({ isLoggedIn, userInfo }));
  }

  static isTokenExpired(decoded) {
    if (!decoded || !decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  }
}

// Run once immediately — equivalent to the static block
AuthService._init();

export default AuthService;